/**
 * Saltar a otro paso (el stepper, «Tu campaña», «Atrás») con algo sin guardar en éste (29/10/2026): se pregunta en la
 * misma pantalla, nunca con `window.confirm`. «Guardar y seguir» hace lo de «Siguiente» y va al paso pedido (o al más
 * lejano que deje lo guardado); «Descartar» vuelve a lo guardado y va; «Quedarme» no hace nada. Si todavía no se puede
 * guardar, «Guardar y seguir» queda apagado y dice por qué. Antes, el salto no guardaba ni avisaba: «Revisar y mandar»
 * mostraba lo guardado y el operador creía que salía lo de la pantalla.
 */
import { useEffect, useId, useRef } from "react";
import type { PasoAsistente } from "../../logic/chequeo";
import { NOMBRE_DEL_PASO } from "../../logic/tuCampana";

export const preguntaSinGuardar = (paso: PasoAsistente) => `Tenés cambios sin guardar en «${NOMBRE_DEL_PASO[paso]}». ¿Guardar y seguir?`;

export function PreguntaSinGuardar({ paso, noSeGuarda, guardando, onGuardar, onDescartar, onQuedarme }: {
  /** el paso que tiene los cambios (el de ahora) */
  paso: PasoAsistente;
  /** por qué todavía no se puede guardar (null si se puede) */
  noSeGuarda: string | null;
  guardando: boolean;
  onGuardar: () => void; onDescartar: () => void; onQuedarme: () => void;
}) {
  const idPregunta = useId();
  const idMotivo = useId();
  const caja = useRef<HTMLDivElement>(null);
  // se pidió desde el panel o el stepper, que pueden estar lejos: la pregunta se trae a la vista
  useEffect(() => { caja.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" }); }, []);
  return (
    <div ref={caja} role="group" aria-labelledby={idPregunta} data-testid="pregunta-sin-guardar"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <div className="flex-1 min-w-[200px]">
        <p id={idPregunta} className="font-medium">{preguntaSinGuardar(paso)}</p>
        {noSeGuarda && <p id={idMotivo} className="text-xs">Todavía no se puede guardar: {noSeGuarda.charAt(0).toLowerCase()}{noSeGuarda.slice(1)}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={onGuardar} disabled={guardando || !!noSeGuarda} aria-describedby={noSeGuarda ? idMotivo : undefined}
          className="h-11 rounded-lg bg-amber-600 px-4 font-semibold text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed">
          Guardar y seguir
        </button>
        <button type="button" onClick={onDescartar} disabled={guardando}
          className="h-11 rounded-lg border border-amber-300 bg-white px-4 font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50">
          Descartar
        </button>
        <button type="button" onClick={onQuedarme} disabled={guardando}
          className="h-11 rounded-lg px-4 font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50">
          Quedarme
        </button>
      </div>
    </div>
  );
}
